import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCheck,
  IconClock,
  IconCopy,
  IconDeviceDesktop,
  IconExternalLink,
  IconEye,
  IconRefresh,
  IconStairsUp,
  IconTrash,
  IconUpload,
  IconUsers,
} from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { fetchPageAnalyticsDetail, deleteHostedPage, resetPageAnalytics, uploadHostedPage } from '../services/api';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
function formatCountry(code) {
  if (!code) return null;
  try { return regionNames.of(code); } catch { return code; }
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

const RANGE_OPTIONS = [
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: 'All time', value: 'all' },
];

export default function HostedPageDetailPage() {
  const { slug } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [deleting, setDeleting] = useState(false);
  const [resetOpened, { open: openReset, close: closeReset }] = useDisclosure(false);
  const [resetting, setResetting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const fileInputRef = useRef(null);

  async function handleReplaceFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;
    setReplacing(true);
    try {
      const result = await uploadHostedPage(api, file, slug);
      notifications.show({
        title: 'Content replaced',
        message: `"${result.title || slug}" updated${result.assetCount ? ` with ${result.assetCount} assets` : ''}. URL and analytics are unchanged.`,
        color: 'teal',
      });
      loadData();
    } catch (err) {
      notifications.show({
        title: 'Failed to replace content',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setReplacing(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      await resetPageAnalytics(api, slug);
      notifications.show({ title: 'Analytics reset', message: 'All visit data has been cleared.', color: 'orange' });
      closeReset();
      loadData();
    } catch (err) {
      notifications.show({
        title: 'Failed to reset analytics',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setResetting(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteHostedPage(api, slug);
      notifications.show({ title: 'Page deleted', message: `"${data?.title || slug}" has been removed.`, color: 'teal' });
      navigate('/hosting');
    } catch (err) {
      notifications.show({
        title: 'Failed to delete page',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
      setDeleting(false);
      closeDelete();
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await fetchPageAnalyticsDetail(api, slug, range);
      setData(detail);
    } catch (err) {
      notifications.show({
        title: 'Failed to load page analytics',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [api, slug, range]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.summary || {};
  const maxDayViews = Math.max(1, ...(data?.timeSeries || []).map((d) => d.views));
  const totalDevice = (data?.deviceBreakdown || []).reduce((sum, d) => sum + d.count, 0) || 1;
  const maxRef = Math.max(1, ...(data?.topReferrers || []).map((r) => r.count));

  const statCards = [
    { label: 'Total Views', icon: IconEye, value: summary.totalViews ?? 0, color: 'aviary' },
    { label: 'Unique Visitors', icon: IconUsers, value: summary.uniqueVisitors ?? 0, color: 'teal' },
    { label: 'Avg. Time on Page', icon: IconClock, value: formatDuration(summary.avgTimeOnPage), color: 'grape' },
    { label: 'Avg. Scroll Depth', icon: IconStairsUp, value: `${summary.avgScroll || 0}%`, color: 'orange' },
  ];

  return (
    <Box p="md" bg="white" h="100%">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate('/hosting')}>
              <IconArrowLeft size={18} />
            </ActionIcon>
            <div>
              <Text fw={700} size="lg">{data?.title || slug}</Text>
              {data?.url && (
                <Group gap={4} wrap="nowrap">
                  <Anchor href={data.url} target="_blank" rel="noopener noreferrer" size="xs" c="dimmed">
                    {data.url}
                  </Anchor>
                  <CopyButton value={data.url} timeout={1500}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy URL'} withArrow>
                        <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy} size="sm">
                          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                  <ActionIcon variant="subtle" color="gray" component="a" href={data.url} target="_blank" rel="noopener noreferrer" size="sm">
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Group>
              )}
            </div>
          </Group>
          <Group gap="xs">
            <SegmentedControl size="xs" data={RANGE_OPTIONS} value={range} onChange={setRange} />
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,.zip"
              style={{ display: 'none' }}
              onChange={handleReplaceFile}
            />
            <Tooltip label="Upload a new .html or .zip — same URL, analytics kept" withArrow>
              <Button
                size="xs"
                color="teal"
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={replacing}
                onClick={() => fileInputRef.current?.click()}
              >
                Replace Content
              </Button>
            </Tooltip>
            <Button size="xs" color="orange" variant="light" leftSection={<IconRefresh size={14} />} onClick={openReset}>
              Reset Analytics
            </Button>
            <Button size="xs" color="red" variant="light" leftSection={<IconTrash size={14} />} onClick={openDelete}>
              Delete
            </Button>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          {statCards.map((card) => (
            <Paper key={card.label} radius="md" p="lg" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed" fw={500}>{card.label}</Text>
                <ThemeIcon variant="light" color={card.color} size="lg" radius="md">
                  <card.icon size={18} />
                </ThemeIcon>
              </Group>
              <Text fw={700} size="xl">{card.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Paper radius="md" p="lg" withBorder>
            <Text fw={600} size="sm" mb="md">Views over time</Text>
            {(data?.timeSeries || []).length === 0 ? (
              <Text c="dimmed" size="sm">No visits in this range.</Text>
            ) : (
              <Stack gap="xs">
                {data.timeSeries.map((d) => (
                  <Group key={d.day} gap="sm" wrap="nowrap">
                    <Text size="xs" c="dimmed" w={80} style={{ flexShrink: 0 }}>{d.day}</Text>
                    <Progress value={(d.views / maxDayViews) * 100} color="aviary" size="lg" radius="sm" style={{ flex: 1 }} />
                    <Text size="xs" fw={500} w={32} ta="right" style={{ flexShrink: 0 }}>{d.views}</Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper radius="md" p="lg" withBorder>
            <Group gap="xs" mb="md">
              <IconDeviceDesktop size={16} />
              <Text fw={600} size="sm">Devices</Text>
            </Group>
            {(data?.deviceBreakdown || []).length === 0 ? (
              <Text c="dimmed" size="sm">No data yet.</Text>
            ) : (
              <Stack gap="xs">
                {data.deviceBreakdown.map((d) => (
                  <Group key={d.device} gap="sm" wrap="nowrap">
                    <Text size="xs" tt="capitalize" w={80} style={{ flexShrink: 0 }}>{d.device}</Text>
                    <Progress value={(d.count / totalDevice) * 100} color="teal" size="lg" radius="sm" style={{ flex: 1 }} />
                    <Text size="xs" fw={500} w={64} ta="right" style={{ flexShrink: 0 }}>
                      {d.count} ({Math.round((d.count / totalDevice) * 100)}%)
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>
        </SimpleGrid>

        <Paper radius="md" p="lg" withBorder>
          <Text fw={600} size="sm" mb="md">Top referrers</Text>
          {(data?.topReferrers || []).length === 0 ? (
            <Text c="dimmed" size="sm">No referrers yet.</Text>
          ) : (
            <Stack gap="xs">
              {data.topReferrers.map((r) => (
                <Group key={r.referrer} gap="sm" wrap="nowrap">
                  <Text size="xs" w={220} style={{ flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.referrer}
                  </Text>
                  <Progress value={(r.count / maxRef) * 100} color="grape" size="lg" radius="sm" style={{ flex: 1 }} />
                  <Text size="xs" fw={500} w={32} ta="right" style={{ flexShrink: 0 }}>{r.count}</Text>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        <Paper radius="md" p="lg" withBorder>
          <Text fw={600} size="sm" mb="md">Recent visits</Text>
          {(data?.recentVisits || []).length === 0 ? (
            <Text c="dimmed" size="sm">No visits yet.</Text>
          ) : (
            <ScrollArea>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Device</Table.Th>
                    <Table.Th>Country</Table.Th>
                    <Table.Th>Referrer</Table.Th>
                    <Table.Th>Source</Table.Th>
                    <Table.Th style={{ textAlign: 'center' }}>Time on Page</Table.Th>
                    <Table.Th style={{ textAlign: 'center' }}>Scroll</Table.Th>
                    <Table.Th style={{ textAlign: 'center' }}>Returning</Table.Th>
                    <Table.Th>User Agent</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.recentVisits.map((v, i) => (
                    <Table.Tr key={`${v.ts}-${i}`}>
                      <Table.Td><Text size="xs">{formatDate(v.ts)}</Text></Table.Td>
                      <Table.Td><Text size="xs" tt="capitalize">{v.device}</Text></Table.Td>
                      <Table.Td><Text size="xs">{formatCountry(v.country) || <Text span c="dimmed">—</Text>}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{v.referrer}</Text></Table.Td>
                      <Table.Td>
                        {v.linkId
                          ? <Badge size="xs" variant="light" color="aviary" style={{ fontFamily: 'monospace' }}>{v.linkId}</Badge>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        <Text size="xs">{v.elapsedSeconds != null ? formatDuration(v.elapsedSeconds) : '—'}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        <Text size="xs">{v.maxScrollPct != null ? `${v.maxScrollPct}%` : '—'}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        {v.isReturn ? <Badge size="xs" variant="light" color="teal">Return</Badge> : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.userAgent}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      </Stack>

      <Modal opened={resetOpened} onClose={closeReset} title="Reset analytics" size="sm" centered>
        <Stack gap="sm">
          <Text size="sm">
            This will permanently delete all visit data for <Text span fw={600}>"{data?.title || slug}"</Text>. Use this before going live to clear test traffic. This cannot be undone.
          </Text>
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeReset}>Cancel</Button>
            <Button color="orange" loading={resetting} onClick={handleReset}>Reset counts</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete page" size="sm" centered>
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to delete <Text span fw={600}>"{data?.title || slug}"</Text>? This will remove the page and all its assets. This cannot be undone.
          </Text>
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeDelete}>Cancel</Button>
            <Button color="red" loading={deleting} onClick={handleDelete}>Delete page</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
